import kebabCase from "lodash.kebabcase";
import replace from "lodash.replace";
import { ALLOWED_IMAGE_TYPES, BADGE_DATE_TIME_FORMAT, QR_VISITOR_TYPES } from "common/constants";
import hashUtils from "common/utils/hashUtils";
import dayjs from "common/dayjs";
import { IPersonBadge } from "common/types";
import { IQRCodeEntity } from "./types";
import { v4 as uuid } from "uuid";
import { getDateTicks } from "../../../../../common/utils/dateUtils";

export const getPDFName = (displayName: string) =>
  `${kebabCase(replace(displayName, /[^\p{L} ]/gu, "").trim())}_badge_${dayjs(new Date()).format(BADGE_DATE_TIME_FORMAT)}.pdf`;

export const findAllowedTypeInContentType = (contentType: string) => ALLOWED_IMAGE_TYPES.find(type => contentType.includes(type));

export const getIdInBrackets = (companyName: string, standId: string) =>
  40 - companyName.length - 3 >= standId.length ? " (" + standId + ")" : "";

export const removeMultipleSpacesFromString = (val: string | undefined) => {
  if (!val) {
    return "";
  }
  return val.replace(/ +(?= )/g, "");
};

const generateQRCodeContent = (gbsCode: string, badgeId: string, firstName: string, version: string, ebsCode: string, secret: string) => {
  if (!secret) {
    return "";
  }
  const contentForHash = `${badgeId}|${version}|${ebsCode}`;
  const content = `${badgeId}|${firstName}|${version}|${ebsCode}`;
  const hash = hashUtils.computeHash(contentForHash, secret, true);
  return `${gbsCode}|${content}|${hash}`;
};

const parseQRCodeString = (qrString: string | undefined, customerType: string): IQRCodeEntity | undefined => {
  if (!qrString) {
    return undefined;
  }
  const valuesArray = qrString.split("|");
  const version = Number(valuesArray && valuesArray[3] && valuesArray[3]!.substring(valuesArray[3]!.indexOf("-") + 1));
  return {
    gbsCode: valuesArray[0]!,
    badgeId: valuesArray[1]!,
    firstName: valuesArray[2]!,
    badgeVersion: isNaN(version) ? 1 : version,
    badgeVersionCode: QR_VISITOR_TYPES[customerType],
    ebs_product_code: valuesArray[4]!,
    hash: valuesArray[5]!
  };
};

const getUpdatedQrContent = (
  currentContent: string | undefined,
  updatedBadge: IPersonBadge,
  entitlementId: string,
  secret: string,
  skipVersion = false
) => {
  const { contact } = updatedBadge;
  try {
    const parsed = parseQRCodeString(currentContent, updatedBadge.customerType)!;
    const { gbsCode, badgeId, badgeVersion, badgeVersionCode } = parsed;
    const updateOffset = skipVersion ? 0 : 1;
    const updatedCode = `${badgeVersionCode}-${updatedBadge?.badgeVersion || badgeVersion + updateOffset}`;
    return generateQRCodeContent(gbsCode, badgeId, contact.firstName, updatedCode, entitlementId, secret);
  } catch (e) {
    return "";
  }
};

const generateBadgeId = () => {
  const maxElapsedTicksLength = 16;
  const beginDate = new Date(2021, 1, 1);
  const currentDate = dayjs(new Date()).utc().toDate();
  const diff = (getDateTicks(currentDate) - getDateTicks(beginDate)).toString();
  return `${diff.padStart(maxElapsedTicksLength, "0").slice(0, maxElapsedTicksLength)}-${uuid().slice(0, 3).toUpperCase()}`;
};

const generateQRContentForNewReg = (
  gbsCode: string,
  firstName: string,
  ebsCode: string,
  secret: string,
  customerType: string,
  id: string
) => {
  const version = `${QR_VISITOR_TYPES[customerType]}-1`;
  return generateQRCodeContent(gbsCode, id, firstName, version, ebsCode, secret);
};

const QRUtils = {
  generateQRCodeContent,
  getUpdatedQrContent,
  parseQRCodeString,
  generateBadgeId,
  generateQRContentForNewReg
};

export default QRUtils;
